import { useEffect, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

const PAGE_SIZE = 100;

/**
 * useCompanyPeople — data half of the company page's "People" rail card.
 *
 * Reads the company's CURRENT, non-company-hidden Promoter roster through the `company_jobs` view
 * (`companyJobs.getAllTeamProfilesByCompany`, the same filters as the Team Members card's query) and
 * pages through every matching row before settling — that query caps a single page at 100 job rows,
 * and a bound cap proves nothing about how many distinct people are behind it, so a card that stopped
 * at page 1 could show a smaller-than-true count for a company with more than 100 current jobs. Rows
 * are jobs, not people: one person holding two current jobs at the company arrives twice, so this
 * hook collapses to distinct people (first occurrence wins) before reporting a count the fetched data
 * can actually prove.
 *
 * Idles (settling empty) until it has a `companyId`, mirroring useTeamMembers/useMediaOutletsRail.
 * Never throws: a decorative rail card must not take the profile page down, so both failure shapes
 * (`getResource` resolving `{ error }` on a non-2xx, and a rejected transport promise) log and settle
 * empty.
 *
 * @param {number|string} [companyId] - The company whose current team to list
 * @returns {{ members: Array, isLoading: boolean }} `members` are `{ id, name, imageUrl, slug, title }`
 *   rows, one per distinct person, in the view's own order
 */
function useCompanyPeople(companyId) {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setMembers([]);
      setIsLoading(false);
      return undefined;
    }

    let componentIsMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);

        const peopleById = new Map();
        let page = 1;
        // PostgREST's `remainingRows` comes from an ESTIMATED count and can under-report, ending the
        // walk before every row has been seen. A page that came back full proves nothing about the
        // count and always earns another request; only a SHORT page (fewer than PAGE_SIZE rows) proves
        // there is no more data. Seed at PAGE_SIZE so the loop always attempts page 1.
        let lastPageRowCount = PAGE_SIZE;

        do {
          // Each page's offset follows from the previous one, so this walk cannot be parallelized.
          // eslint-disable-next-line no-await-in-loop
          const response = await qapi.companyJobs.getAllTeamProfilesByCompany(companyId, {
            page,
            per_page: PAGE_SIZE,
          });
          if (!componentIsMounted) return;

          // getResource RESOLVES `{ error }` on a non-2xx instead of throwing, so THIS branch — not
          // the catch below — is the one an ordinary PostgREST failure takes.
          if (response?.error) {
            qlog.error("Company people lookup failed:", response.error);
            setMembers([]);
            return;
          }

          const rows = Array.isArray(response?.data) ? response.data : [];
          rows.forEach((row) => {
            if (row.person_id == null || peopleById.has(row.person_id)) return;
            peopleById.set(row.person_id, {
              id: row.person_id,
              name: row.person_name,
              imageUrl: row.person_profile_image_url,
              slug: row.person_slug,
              migrateId: row.person_migrate_id,
              title: row.title,
            });
          });

          lastPageRowCount = rows.length;
          page += 1;
        } while (lastPageRowCount === PAGE_SIZE);

        if (!componentIsMounted) return;
        setMembers([...peopleById.values()]);
      } catch (error) {
        // The transport itself failed (network down, token refresh threw) — a rejection, not `{ error }`.
        qlog.error("Company people lookup failed:", error);
        if (componentIsMounted) setMembers([]);
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [companyId]);

  return { members, isLoading };
}

export default useCompanyPeople;
